/**
 * DraftPinField Component
 *
 * A PIN input field with generate button and optional lock, using design system styling.
 */

import React, { useState } from 'react';
import { Grid, Box, Tooltip, IconButton } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { TextField } from './TextField';
import { Button } from '../Button';
import { colors } from '../../tokens';

const PIN_LENGTH = 4;

const validatePin = (pin: string): boolean => {
    if (!pin || pin.length !== PIN_LENGTH) return false;
    if (pin[0] === '0') return false;
    return /^\d+$/.test(pin);
};

const generateRandomPin = (): string => {
    return (Math.floor(Math.random() * (9999 - 1000)) + 1000).toString();
};

export interface DraftPinFieldProps<State extends Record<string, unknown>> {
    /** State key for the PIN value */
    stateKey?: string & keyof State;
    /** Draft state tuple [state, setState] */
    draft: [State, React.Dispatch<React.SetStateAction<State>>];
    /** Whether the field is disabled */
    disabled?: boolean;
    /** Whether to show the lock toggle */
    showLock?: boolean;
    /** Grid size (out of 12) */
    size?: number;
    /** Additional styles */
    sx?: SxProps<Theme>;
}

export const DraftPinField = <State extends Record<string, unknown>>({
    stateKey = 'PIN' as string & keyof State,
    draft,
    disabled = false,
    showLock = false,
    size = 12,
    sx,
}: DraftPinFieldProps<State>) => {
    const [draftState, setDraftState] = draft;
    const [locked, setLocked] = useState(true);
    const [touched, setTouched] = useState(false);

    const currentValue = String(draftState?.[stateKey] ?? '');
    const isValid = validatePin(currentValue);
    const showError = touched && !isValid;
    const isDisabled = disabled || (showLock && locked);

    const handleChange = (value: string) => {
        // Only allow digits
        const digits = value.replace(/\D/g, '').slice(0, PIN_LENGTH);
        setDraftState((prev) => ({
            ...prev,
            [stateKey]: digits,
        }));
    };

    const handleBlur = () => {
        setTouched(true);
    };

    const handleGenerate = () => {
        const newPin = generateRandomPin();
        setDraftState((prev) => ({
            ...prev,
            [stateKey]: newPin,
        }));
        setTouched(true);
    };

    const handleLockToggle = () => {
        if (!locked && !isValid) return; // Don't allow locking with invalid PIN
        setLocked((prev) => !prev);
    };

    return (
        <Grid item xs={12} sm={12} md={size} sx={sx}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ flex: 1, maxWidth: 200 }}>
                    <TextField
                        label='PIN'
                        value={currentValue}
                        onChange={handleChange}
                        type='text'
                        placeholder='Enter 4-digit PIN'
                        disabled={isDisabled}
                        required
                        error={showError}
                        errorMessage={
                            showError
                                ? `Please provide a valid ${PIN_LENGTH} digit PIN not beginning with zero.`
                                : undefined
                        }
                        sx={{
                            '& input': {
                                letterSpacing: '0.25em',
                                fontFamily: 'monospace',
                            },
                        }}
                    />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: '26px' }}>
                    <Button variant='secondary' size='small' onClick={handleGenerate} disabled={isDisabled}>
                        Generate
                    </Button>

                    {showLock && (
                        <Tooltip title={locked ? 'Unlock to manage PIN' : 'Lock PIN changes'}>
                            <IconButton
                                onClick={handleLockToggle}
                                disabled={disabled}
                                size='small'
                                sx={{
                                    color: locked ? colors.neutral[500] : colors.primary[500],
                                    '&:hover': {
                                        backgroundColor: colors.neutral[100],
                                    },
                                }}>
                                {locked ? <LockIcon fontSize='small' /> : <LockOpenIcon fontSize='small' />}
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>
        </Grid>
    );
};

export default DraftPinField;
